import React from "react";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import { dateColumnType } from "components/UI/AcxDataGrid/ColumnTypes/DateColTypes";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";
import { LLMCLassifierDetailsResponse } from "models/LLMs";
import PublishedModelResultsReconcileResult from "models/PublishedModelResultsReconcileResult";
import ClassifierService from "services/ClassifierService";
import LLMClassifierService from "services/LLMClassifierService";
import SignalsPageStore from "components/Signals/Store/SignalsPageStore";
import { BaseStore } from "stores/BaseStore";
import { AcxStore } from "stores/RootStore";
import type { IRootStore } from "stores/RootStore";
import { ShowUnpublishedSwitch } from "components/Classifiers/Common/ShowUnpublishedSwitch";
import { ShowArchivedSwitch } from "components/Classifiers/Common/ShowArchivedSwitch";
import { ServiceError } from "services/BaseService";
import { llmClassifierVersionLabel } from "../utils/llmClassifierVersionDisplay";

@AcxStore
export class LLMClassifiersStore extends BaseStore {
    public static Tasks = {
        LoadLLMClassifiers: "Load LLM Classifiers",
        PublishLLMClassifier: "Publish LLM Classifier",
        UnpublishLLMClassifier: "Unpublish LLM Classifier",
        CopyLLMClassifier: "Copy LLM Classifier",
        DeleteLLMClassifier: "Delete LLM Classifier",
        ArchiveLLMClassifier: "Archive LLM Classifier",
        UnarchiveLLMClassifier: "Unarchive LLM Classifier",
    } as const;

    private readonly taskAbortControllerMap: Map<
        typeof LLMClassifiersStore.Tasks[keyof typeof LLMClassifiersStore.Tasks],
        AbortController
    >;

    private readonly messageStore: MessageStore;

    private readonly classifiersService: ClassifierService;
    private readonly llmClassifierService: LLMClassifierService;

    @observable
    public llmClassifiers: LLMCLassifierDetailsResponse[] = [];
    public dataGridStore: AcxDataGridStore;

    @observable public showUnpublished: boolean = false;
    @observable public showArchived: boolean = false;
    @observable public searchString: string | undefined;

    constructor(private rootStore: IRootStore) {
        super("LLM Classifiers Store");
        makeObservable(this);

        this.messageStore = this.rootStore.getStore(MessageStore);

        this.classifiersService = new ClassifierService();
        this.llmClassifierService = new LLMClassifierService();

        this.dataGridStore = new AcxDataGridStore(
            "LLMClassifiersDataGridStore",
        );
        this.dataGridStore.setColumns(columns);
        this.dataGridStore.hideFilter = true;
        this.dataGridStore.vertIconMenuItemsBuilder = () => [
            {
                id: "show-unpublished",
                label: <ShowUnpublishedSwitch store={this} />,
                props: {
                    onClick: () => this.toggleShowUnpublished(),
                },
            },
            {
                id: "show-archived",
                label: <ShowArchivedSwitch store={this} />,
                props: {
                    onClick: () => this.toggleShowArchived(),
                },
            },
            "Divider",
        ];

        this.taskAbortControllerMap = new Map();
    }

    @computed
    public get singleSelectedLLMClassifier() {
        if (this.dataGridStore.selectedRows.length !== 1) return undefined;
        return this.dataGridStore
            .selectedRows[0] as LLMCLassifierDetailsResponse;
    }

    // Archived models cannot be reclassified (the request service only accepts published, active classifiers).
    // Computeds so the reclassify dialog gets stable array references between renders (AcxSelect re-syncs its
    // displayed value whenever the options/defaultValue references change).
    @computed
    public get reclassifiableLLMClassifiers() {
        return this.llmClassifiers.filter((c) => c.isPublished && c.isActive);
    }

    @computed
    public get selectedReclassifiableLLMClassifiers() {
        // selectedRows is loosely typed (GridValidRowModel), so cast each row the way singleSelectedLLMClassifier does
        return this.dataGridStore.selectedRows
            .map((row) => row as LLMCLassifierDetailsResponse)
            .filter((c) => c.isPublished && c.isActive);
    }

    /**
     * Sets the rows to be shown in the data grid, taking into account
     * `showUnpublished`, `showArchived`, and `searchString`
     */
    @action
    private setDataGridRows() {
        if (
            !this.dataGridStore.gridApi ||
            !this.dataGridStore.gridApi.current
        ) {
            return;
        }

        let newRows: LLMCLassifierDetailsResponse[] = this.llmClassifiers;
        if (!this.showUnpublished && !this.showArchived) {
            newRows = this.llmClassifiers.filter(
                (c) => c.isPublished && c.isActive,
            );
        } else if (this.showUnpublished && !this.showArchived) {
            newRows = this.llmClassifiers.filter((c) => c.isActive);
        } else if (!this.showUnpublished && this.showArchived) {
            newRows = this.llmClassifiers.filter(
                (c) => c.isPublished || !c.isActive,
            );
        } else if (this.showUnpublished && this.showArchived) {
            newRows = this.llmClassifiers;
        }

        if (this.searchString) {
            newRows = newRows.filter((c) =>
                c.name.toLowerCase().includes(this.searchString!.toLowerCase()),
            );
        }

        const currentlySelected = this.dataGridStore.selectedRows;
        const newSelections: string[] = [];
        for (const row of currentlySelected) {
            const existingNewRow = newRows.find(
                (newRow) => newRow.id === row.id,
            );
            if (!existingNewRow) continue;
            newSelections.push(existingNewRow.id);
        }

        this.dataGridStore.rows = newRows;
        this.dataGridStore.handleSelectionChange(newSelections);
    }

    public loadLLMClassifiers() {
        return this.setupAsyncTask(
            LLMClassifiersStore.Tasks.LoadLLMClassifiers,
            async () => {
                try {
                    this.dataGridStore.setLoading(true);

                    const abortController = new AbortController();
                    this.taskAbortControllerMap.set(
                        LLMClassifiersStore.Tasks.LoadLLMClassifiers,
                        abortController,
                    );

                    this.llmClassifiers =
                        await this.llmClassifierService.getLLMClassifiers(
                            abortController.signal,
                        );
                    abortController.signal.throwIfAborted();

                    this.setDataGridRows();
                } finally {
                    this.taskAbortControllerMap.delete(
                        LLMClassifiersStore.Tasks.LoadLLMClassifiers,
                    );
                    this.dataGridStore.setLoading(false);
                }
            },
        );
    }

    public cancelLoadLLMClassifiers() {
        const abortController = this.taskAbortControllerMap.get(
            LLMClassifiersStore.Tasks.LoadLLMClassifiers,
        );
        if (!abortController) return;

        abortController.abort();
        this.taskAbortControllerMap.delete(
            LLMClassifiersStore.Tasks.LoadLLMClassifiers,
        );
    }

    public searchLLMClassifiers(value?: string) {
        this.searchString = value;
        this.setDataGridRows();
    }

    @action
    private toggleShowUnpublished() {
        this.showUnpublished = !this.showUnpublished;
        this.setDataGridRows();
    }

    @action
    private toggleShowArchived() {
        this.showArchived = !this.showArchived;
        this.setDataGridRows();
    }

    public copyLLMClassifier(classifierId: string) {
        return this.setupAsyncTask(
            LLMClassifiersStore.Tasks.CopyLLMClassifier,
            async () => {
                try {
                    const llmClassifier =
                        await this.llmClassifierService.copyLLMClassifier(
                            classifierId,
                        );
                    this.messageStore.logMessage("Model copied", "success");
                    return llmClassifier;
                } catch (error) {
                    if (error instanceof Error)
                        this.messageStore.logError(
                            "Failed to copy model: " + error.message,
                        );
                }
            },
        );
    }

    private async reloadAffectedPublishedModelResultsDashboards(
        result: PublishedModelResultsReconcileResult | null | undefined,
    ) {
        const signalsPageStore = this.rootStore.getStore(SignalsPageStore);
        signalsPageStore.invalidatePublishedModelResultsCharts();
        await signalsPageStore.reloadDashboardsById(
            result?.affectedDashboardIds ?? [],
        );
    }

    public publishLLMClassifier(
        classifierId: string,
        implementationId: string,
        afterPublish?: (result: PublishedModelResultsReconcileResult) => void,
    ) {
        return this.setupAsyncTask(
            LLMClassifiersStore.Tasks.PublishLLMClassifier,
            async () => {
                try {
                    const result =
                        await this.llmClassifierService.publishLLMClassifier(
                            classifierId,
                            implementationId,
                        );
                    await this.reloadAffectedPublishedModelResultsDashboards(
                        result,
                    );
                    runInAction(() => {
                        const targeted = this.llmClassifiers.find(
                            (c) => c.id === classifierId,
                        );
                        if (targeted) {
                            targeted.isPublished = true;
                            targeted.isActive = true;
                        }
                        this.setDataGridRows();
                        if (afterPublish && !!result?.dashboardId)
                            afterPublish(result);
                    });
                    return result;
                } catch (error) {
                    if (error instanceof Error)
                        this.messageStore.logError(error.message);
                }
            },
        );
    }

    public unpublishLLMClassifier(classifierId: string) {
        return this.setupAsyncTask(
            LLMClassifiersStore.Tasks.UnpublishLLMClassifier,
            async () => {
                try {
                    const result =
                        await this.llmClassifierService.unpublishLLMClassifier(
                            classifierId,
                        );
                    await this.reloadAffectedPublishedModelResultsDashboards(
                        result,
                    );

                    runInAction(() => {
                        const targeted = this.llmClassifiers.find(
                            (c) => c.id === classifierId,
                        );
                        if (targeted) {
                            targeted.isPublished = false;
                            targeted.isActive = false;
                        }
                        if (!this.showUnpublished)
                            this.dataGridStore.removeAllSelectedRows();
                        this.setDataGridRows();
                    });
                    this.messageStore.logMessage(
                        "Model unpublished",
                        "success",
                    );
                    // Returned so callers can tell success from a swallowed server error, as publish does.
                    return result;
                } catch (error) {
                    if (error instanceof Error)
                        this.messageStore.logError(error.message);
                }
            },
        );
    }

    public deleteLLMClassifier(classifierId: string) {
        return this.setupAsyncTask(
            LLMClassifiersStore.Tasks.DeleteLLMClassifier,
            async () => {
                try {
                    await this.llmClassifierService.deleteLLMClassifier(
                        classifierId,
                    );

                    const result =
                        await this.classifiersService.removePublishedModelResultsSignalsGraphs(
                            classifierId,
                        );
                    await this.reloadAffectedPublishedModelResultsDashboards(
                        result,
                    );
                    runInAction(() => {
                        this.dataGridStore.removeSelectedById(classifierId);
                        this.llmClassifiers = this.llmClassifiers.filter(
                            (c) => c.id !== classifierId,
                        );
                        this.setDataGridRows();
                    });
                    this.messageStore.logMessage("Model deleted", "success");
                } catch (error) {
                    if (error instanceof ServiceError) {
                        if (error.response.status === 409)
                            this.messageStore.logError(
                                "Model could not be deleted because existing Classifier Results exist!",
                            );
                        else this.messageStore.logError(error.message);
                    }
                }
            },
        );
    }

    public archiveLLMClassifier(classifierId: string) {
        return this.setupAsyncTask(
            LLMClassifiersStore.Tasks.ArchiveLLMClassifier,
            async () => {
                try {
                    await this.llmClassifierService.archiveLLMClassifier(
                        classifierId,
                    );

                    const result =
                        await this.classifiersService.removePublishedModelResultsSignalsGraphs(
                            classifierId,
                        );
                    await this.reloadAffectedPublishedModelResultsDashboards(
                        result,
                    );
                    runInAction(() => {
                        const targeted = this.llmClassifiers.find(
                            (c) => c.id === classifierId,
                        );
                        if (targeted) targeted.isActive = false;
                        if (!this.showArchived)
                            this.dataGridStore.removeAllSelectedRows();
                        this.setDataGridRows();
                    });
                    this.messageStore.logMessage("Model archived", "success");
                } catch (error) {
                    if (error instanceof ServiceError)
                        this.messageStore.logError(
                            error.errorMessage ?? error.message,
                        );
                }
            },
        );
    }

    public unarchiveLLMClassifier(classifierId: string) {
        return this.setupAsyncTask(
            LLMClassifiersStore.Tasks.UnarchiveLLMClassifier,
            async () => {
                try {
                    const unarchiveInfo =
                        await this.llmClassifierService.unarchiveLLMClassifier(
                            classifierId,
                        );

                    if (unarchiveInfo.isPublished) {
                        const result =
                            await this.classifiersService.createPublishedModelResultsSignalsGraphs(
                                classifierId,
                            );
                        await this.reloadAffectedPublishedModelResultsDashboards(
                            result,
                        );
                    }

                    runInAction(() => {
                        const targeted = this.llmClassifiers.find(
                            (c) => c.id === classifierId,
                        );
                        if (targeted) {
                            targeted.isActive = true;
                            targeted.isPublished = unarchiveInfo.isPublished;
                        }
                        this.setDataGridRows();
                    });
                    this.messageStore.logMessage("Model unarchived", "success");
                } catch (error) {
                    if (error instanceof ServiceError)
                        this.messageStore.logError(
                            error.errorMessage ?? error.message,
                        );
                }
            },
        );
    }
}

const columns: IColDef[] = [
    {
        field: "id",
        headerName: "Id",
        hide: true,
    },
    {
        field: "name",
        headerName: "Model Name",
        flex: 1,
    },
    {
        field: "displayedVersionCreatedOn",
        headerName: "Version",
        flex: 0.25,
        valueGetter: (_, row) =>
            llmClassifierVersionLabel(row.displayedVersionCreatedOn),
    },
    {
        field: "modifiedOn",
        headerName: "Last Updated",
        flex: 0.5,
        ...dateColumnType,
    },
    {
        field: "modifiedBy",
        headerName: "Last Updated By",
        flex: 0.5,
        valueGetter: (_, row) => {
            return row.modifiedBy ?? row.createdBy;
        },
    },
    {
        field: "createdBy",
        headerName: "Owner",
        flex: 0.5,
        hide: true,
    },
    {
        field: "classifierGroup",
        headerName: "Group",
        flex: 0.5,
        valueGetter: (_, row) => {
            return (
                row.classifierGroup?.parentGroup?.name ??
                row.classifierGroup?.name ??
                ""
            );
        },
    },
    {
        field: "classifierSubGroup",
        headerName: "Subgroup",
        flex: 0.5,
        valueGetter: (_, row) => {
            return !!row.classifierGroup?.parentGroup
                ? row.classifierGroup?.name ?? ""
                : "";
        },
    },
    {
        field: "status",
        headerName: "Status",
        flex: 0.25,
        valueGetter: (_, row) => {
            const status = row.isPublished ? "Published" : "Saved";
            if (row.isActive) return status;

            return `Archived (${status})`;
        },
    },
    {
        field: "smartPredict",
        headerName: "SmartPredict",
        flex: 0.25,
        valueGetter: (_, row) =>
            row.smartPredictConfiguration?.length > 0 ? "Active" : "",
    },
];
